import styled from 'styled-components';

/**
 * Navbar nas cores do ConLicitaGO, ativa em /conlicita_go.
 *
 * A página marca data-tema="go" no elemento raiz (e desmarca ao sair), e as
 * regras abaixo repintam o header. Sem isso ficaria uma navbar azul-petróleo
 * sobre um app escuro e laranja — o embarque perderia a unidade visual que a
 * direção aprovou ao escolher manter a identidade do GO.
 *
 * O escopo é por atributo na raiz porque o header é irmão da página no shell —
 * a página não alcança ele por hierarquia.
 */
const TEMA_GO = {
  fundo: '#171717',
  hover: '#2a2a2a',
  acento: '#FF6B00',
  botao: 'linear-gradient(90deg, #FF6B00 0%, #EB2727 100%)',
};

export const Nav = styled.nav`
  min-height: 56px;
  box-shadow: 0 0.25rem 0.75rem rgb(0 0 0 / 5%);

  &.bg-header {
    background: ${(props) => props.theme.$dark};
    color: ${(props) => props.theme.$white};
  }

  /* Repinta a barra no tema GO. Sem prender a uma classe: a barra do usuário
     logado alterna entre bg-header e representant-header conforme o perfil, e
     a regra presa a uma delas não pegava metade das contas. O && apenas dobra
     a especificidade, para vencer as duas variantes. */
  html[data-tema='go'] && {
    background: ${TEMA_GO.fundo};
  }

  html[data-tema='go'] & .btn-menu:hover,
  html[data-tema='go'] & .new-header-chatbot button:hover {
    background: ${TEMA_GO.hover} !important;
  }

  html[data-tema='go'] & .new-header-chatbot button {
    background: ${TEMA_GO.fundo} !important;
  }

  html[data-tema='go'] & .nav-link.active,
  html[data-tema='go'] & .dropdown-toggle:hover {
    color: ${TEMA_GO.acento} !important;
  }

  /* Ajuda e btn-primary do Bootstrap (azul). No tema GO acompanha o gradiente
     laranja dos botões do app, que e o elemento colorido mais visivel da barra. */
  html[data-tema='go'] & .tour-ajuda-button {
    background: ${TEMA_GO.botao} !important;
    border-color: transparent !important;
  }

  html[data-tema='go'] & .tour-ajuda-button:hover {
    filter: brightness(1.08);
  }

  .box-logo {
    padding: 0.5em 1em;
  }

  .btn-menu {
    display: flex;
    height: 56px;
    align-items: center;
    padding: 0 1rem;
    color: ${(props) => props.theme.$white} !important;

    &:hover {
      text-decoration: none;
      background: ${(props) => props.theme.$dark_medium};
    }
  }

  .new-header-chatbot {
    button {
      padding: 0 1rem;
      background: ${(props) => props.theme.$dark} !important;
      border: 0;

      &:hover {
        background: ${(props) => props.theme.$dark_medium} !important;
      }

      .icon-ia {
        height: 25px;
        width: 25px;
        display: inline-block;
        border-color: ${(props) => props.theme.$white};
      }
    }
  }

  .icon-header .notification-bell,
  .icon-header .menu-monitoring {
    color: ${(props) => props.theme.$white} !important;

    &:hover {
      text-decoration: none;
      opacity: 0.8;
    }
  }

  .notification-badge[data-notification-badge]:after {
    border-color: ${(props) => props.theme.$dark};
  }

  .nav-link {
    color: ${(props) => props.theme.$white};
  }

  .nav-link-menu {
    height: 56px;

    &:hover {
      background: ${(props) => props.theme.$dark_medium} !important;
    }
  }

  /* Barra do embaixador (convite aceito): degradê verde do protótipo no lugar do azul. */
  &.ambassador-header {
    background: linear-gradient(90deg, #085d47 0%, #098b72 52.404%, #085c45 100%);

    .new-header-chatbot button {
      background: transparent !important;
    }

    .btn-menu:hover,
    .nav-link-menu:hover,
    .new-header-chatbot button:hover {
      background: rgba(255, 255, 255, 0.12) !important;
    }

    .notification-badge[data-notification-badge]:after {
      border-color: #08684f;
    }
  }

  &.representant-header {
    background: ${(props) => props.theme.$japanese_indigo};

    @media only screen and (max-width: 768px) {
      min-height: 112px;
    }

    button,
    .new-header-chatbot button {
      background: ${(props) => props.theme.$japanese_indigo} !important;
    }

    .btn-menu:hover,
    .nav-link-menu:hover,
    .new-header-chatbot button:hover {
      background: ${(props) => props.theme.$yankees_blue} !important;
    }

    .notification-badge[data-notification-badge]:after {
      border-color: ${(props) => props.theme.$japanese_indigo};
    }

    .representant-select {
      width: 25%;

      @media only screen and (max-width: 768px) {
        width: 100%;
      }
    }
  }

  .img-logos {
    max-width: 100%;

    &.logo-parceiro {
      background: ${(props) => props.theme.$white};
      padding: 5px;
      border-radius: 4px;
    }
  }

  .dropdown-menu {
    position: absolute !important;
    top: 56px;

    &.dropdown-menu-right {
      right: 5px;
    }

    &.dropdown-menu-left {
      left: 0;
    }

    &.representant-tools-menu {
      top: 100%;
      margin-top: 0;
      border-top: 0;
      border-top-left-radius: 0;
      border-top-right-radius: 0;
    }
  }

  @media only screen and (max-width: 768px) {
    margin-left: 0px;
  }

  &.hasTopBanner {
    top: 80px;

    @media only screen and (max-width: 768px) {
      top: 240px;
    }
  }

  &.hasAmbassadorBanner {
    top: 52px;

    @media only screen and (max-width: 768px) {
      top: 72px;
    }
  }
  .menu-tools {
    position: absolute;
    top: 100%;
    left: 0;
    width: 250px;
    background: white;
    border-radius: 8px;
    box-shadow: 0px 4px 6px rgba(0, 0, 0, 0.1);
    padding: 10px;
    z-index: 1000;
  }

  .btn-menu.tools,
  .btn-menu.tools:hover {
    background: ${(props) => props.theme.$white} !important;
    color: ${(props) => props.theme.$dark} !important;
  }

  .user-avatar {
    width: 32px;
    height: 32px;
    background: ${(props) => props.theme.$white};
    color: ${(props) => props.theme.$dark};
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 13px;
    font-weight: bold;
  }

  .user-dropdown {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .dropdown-menu .locked-overlay .dropdown-item {
    padding-right: 40px;
  }

  .dropdown-menu .locked-overlay .lock-badge {
    top: 50%;
    right: 12px;
    bottom: auto;
    transform: translateY(-50%);
  }
`;

export const Box = styled.div`
  background: ${(props) => props.theme.$black}60;
  width: 100%;
  height: 0;
  position: absolute;
  bottom: 0;
  left: 0;
  z-index: 999;

  &.menu-open {
    height: calc(100vh - 56px);
  }
`;
